import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { HIGHLIGHT_FILL_HANDLE_CLASS } from '@databases/highlights/constants';

import { type SelectionState } from '../../machine/state/types';
import { createEffect } from '../utils/create-effect';

import { FILL_HANDLE_LAYER_Z_INDEX } from './constants';

/**
 * Creates the effect that draws the fill handle (the little blue circle)
 * at the active selection.
 *
 * Listens to the same selection and editing deltas as the highlights effect
 * so the handle stays in sync and hides during selection preview and cell edit.
 * (Redrawing on layout resize is handled autonomously by the HighlightsController).
 */
export const createFillHandleEffect = createEffect((controller: HighlightsController) => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------
  let scrollableContainer: HTMLElement | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------
  // Dedicated layer for the fill handle indicator
  let fillHandleLayer: HighlightsRenderer | null = null;

  function ensureDependentElementsAndRenderer() {
    if (!scrollableContainer) {
      scrollableContainer = controller.getScrollContainer();
    }

    if (!fillHandleLayer && scrollableContainer) {
      fillHandleLayer = controller.createLayer();
    }

    return !!(fillHandleLayer && scrollableContainer);
  }

  function syncFillHandleFromState(state: SelectionState, layer: HighlightsRenderer) {
    if (state.selections.length > 0 && state.mode !== 'idle') {
      const activeSelection = state.selections[state.selections.length - 1];

      layer.clear();
      layer.add({
        range: {
          id: 'fill-handle-wrapper',
          anchor: {
            row: activeSelection.anchor.row.index,
            col: activeSelection.anchor.col.index,
          },
          lead: {
            row: activeSelection.lead.row.index,
            col: activeSelection.lead.col.index,
          },
        },
        mode: state.mode,
        zIndex: FILL_HANDLE_LAYER_Z_INDEX,
        className: HIGHLIGHT_FILL_HANDLE_CLASS,
      });
    } else {
      layer.clear();
    }
  }

  return {
    deltaTypes: [
      'selectionModified',
      'selectionReplaced',
      'selectionPreview',
      'cellEditRequested',
      'cellEditStopped',
    ],

    dispose() {
      // Cleanup the fill handle layer
      if (fillHandleLayer) {
        fillHandleLayer.dispose();
        controller.removeLayer(fillHandleLayer);
        fillHandleLayer = null;
      }

      scrollableContainer = null;
    },

    onDelta(delta, state) {
      if (
        !ensureDependentElementsAndRenderer() ||
        !fillHandleLayer ||
        !scrollableContainer
      ) {
        return;
      }

      if (delta.kind === 'selectionPreview') {
        // we don't want to show this when there is a selection preview
        fillHandleLayer.clear();

        return;
      }

      if (delta.kind === 'cellEditRequested') {
        fillHandleLayer.hideAll();

        return;
      }

      if (delta.kind === 'cellEditStopped') {
        fillHandleLayer.showAll();

        return;
      }

      // Handle Fill Handle placement
      syncFillHandleFromState(state, fillHandleLayer);
    },
  };
});
